import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, KeyRound, Edit2, UserX, UserCheck, GraduationCap, Loader2, Trash2 } from 'lucide-react';
import { api, extractError } from '../lib/api';
import { useConfirm } from '../lib/confirm';
import PersonDialog from '../components/PersonDialog';
import { fmtDate } from '../lib/date';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { useAuth } from '../lib/auth';

const WEEKDAYS = [
  { value: 0, label: 'Mo', full: 'Montag' },
  { value: 1, label: 'Di', full: 'Dienstag' },
  { value: 2, label: 'Mi', full: 'Mittwoch' },
  { value: 3, label: 'Do', full: 'Donnerstag' },
  { value: 4, label: 'Fr', full: 'Freitag' },
  { value: 5, label: 'Sa', full: 'Samstag' },
];

export default function EmployeesPage() {
  const { can } = useAuth();
  const { confirm } = useConfirm();
  const canCreate = can('employees', 'create');
  const canEdit = can('employees', 'edit');
  const canDelete = can('employees', 'delete');
  const [items, setItems] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  // Anlegen erzeugt immer Zugang UND Mitarbeiter – gemeinsamer Dialog.
  const [anlegenOpen, setAnlegenOpen] = useState(false);
  // Zugang für einen Altbestand-Mitarbeiter ohne Konto nachreichen.
  const [kontoFuer, setKontoFuer] = useState(null);
  const [schoolOpen, setSchoolOpen] = useState(false);
  const [schoolEmp, setSchoolEmp] = useState(null);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [e, c] = await Promise.all([
        api.get('/employees', { params: { include_inactive: true } }),
        api.get('/cost-centers', { params: { include_inactive: true } }),
      ]);
      setItems(e.data); setCostCenters(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => setAnlegenOpen(true);
  const openEdit = (e) => { setEditing({ ...e }); setEditOpen(true); };

  // Nur noch Bearbeiten: Neue Mitarbeiter entstehen ausschließlich über
  // PersonDialog, weil dabei zwingend auch ein Zugang angelegt wird.
  const save = async () => {
    if (!editing?.id) return;
    try {
      const { id, created_at, has_account, account_email, account_username, account_active, ...rest } = editing;
      await api.patch(`/employees/${id}`, rest);
      toast.success('Mitarbeiter aktualisiert.');
      setEditOpen(false);
      await load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const toggleActive = async (e) => {
    try {
      await api.patch(`/employees/${e.id}`, { active: !e.active });
      if (e.active) {
        // Deaktivieren sperrt auch den Zugang – das muss man dem Anwender
        // sagen, sonst wundert er sich später, warum die Anmeldung nicht geht.
        toast.success(e.has_account
          ? 'Mitarbeiter deaktiviert und Zugang gesperrt.'
          : 'Mitarbeiter deaktiviert.');
      } else {
        toast.success(e.has_account && e.account_active === false
          ? 'Mitarbeiter aktiviert. Der Zugang bleibt gesperrt – unter Einstellungen → Benutzer freigeben.'
          : 'Mitarbeiter aktiviert.');
      }
      await load();
    } catch (err) { toast.error(extractError(err)); }
  };

  // Löschen heißt wirklich löschen – aber nur, solange keine Historie
  // dranhängt. Hängt welche dran, deaktiviert der Server und sagt warum.
  const loeschen = async (e) => {
    if (!(await confirm({
      title: `${e.first_name} ${e.last_name} löschen?`,
      description: 'Der Mitarbeiter wird samt Zugang entfernt. Sind Einsätze, '
        + 'Abwesenheiten oder Zeiten erfasst, bleibt der Datensatz erhalten und '
        + 'wird nur deaktiviert – damit vergangene Monate nachvollziehbar bleiben.',
      variant: 'destructive', confirmLabel: 'Löschen',
    }))) return;
    try {
      const r = await api.delete(`/employees/${e.id}`);
      // Zwei mögliche Ausgänge, deshalb die Meldung vom Server übernehmen.
      if (r.data?.geloescht) toast.success(r.data.message);
      else toast.warning(r.data?.message || 'Mitarbeiter deaktiviert.');
      await load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const openSchool = (e) => { setSchoolEmp(e); setSchoolOpen(true); };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
          <CardTitle className="font-display text-lg">Mitarbeiterverwaltung</CardTitle>
          {canCreate && <Button onClick={openCreate} data-testid="create-employee-button"><Plus size={14} className="mr-1"/>Mitarbeiter anlegen</Button>}
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Typ</TableHead>
                <TableHead>Kostenstelle</TableHead>
                <TableHead>Eintritt</TableHead>
                <TableHead>Enddatum</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Zugang</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(e => {
                const kst = costCenters.find(c => c.id === e.cost_center_id);
                return (
                  <TableRow key={e.id} data-testid={`employee-row-${e.id}`}>
                    <TableCell className="font-semibold">{e.first_name} {e.last_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" style={e.type === 'Azubi' ? { background: '#fef3c7', color: '#92400e', borderColor: '#fbbf24' } : {}}>
                        {e.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {kst ? (<span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: kst.color }}/>{kst.short_code}</span>) : '-'}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{fmtDate(e.start_date)}</TableCell>
                    <TableCell className="font-mono text-sm">{e.end_date ? fmtDate(e.end_date) : '-'}</TableCell>
                    <TableCell>{e.active ? <Badge className="bg-emerald-100 text-emerald-900 border-emerald-200 hover:bg-emerald-100">Aktiv</Badge> : <Badge variant="outline">Inaktiv</Badge>}</TableCell>
                    <TableCell>
                      {/* Die Anmeldekennung liefert der Server nur an
                          Administratoren. Alle anderen sehen lediglich, dass
                          ein Zugang existiert. */}
                      {e.has_account
                        ? <span className="text-xs text-slate-600">
                            {e.account_username || e.account_email || 'vorhanden'}
                            {/* Gesperrt muss sichtbar sein: Sonst sucht man den
                                Grund beim Passwort, obwohl das Konto zu ist. */}
                            {e.account_active === false && (
                              <Badge variant="outline" className="ml-1 text-rose-700 border-rose-300 bg-rose-50">
                                gesperrt
                              </Badge>
                            )}
                          </span>
                        : <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50">kein Zugang</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      {e.type === 'Azubi' && canEdit && (
                        <Button size="sm" variant="ghost" onClick={() => openSchool(e)} title="Schulmuster" data-testid={`school-pattern-button-${e.id}`}><GraduationCap size={14}/></Button>
                      )}
                      {canEdit && <Button size="sm" variant="ghost" onClick={() => openEdit(e)} data-testid={`edit-employee-button-${e.id}`}><Edit2 size={14}/></Button>}
                      {canEdit && !e.has_account && (
                        <Button size="sm" variant="ghost" onClick={() => setKontoFuer(e)}
                                title="Zugang anlegen" data-testid={`create-account-button-${e.id}`}>
                          <KeyRound size={14} className="text-amber-600"/>
                        </Button>
                      )}
                      {canEdit && <Button size="sm" variant="ghost" onClick={() => toggleActive(e)}
                              title={e.active ? 'Deaktivieren (sperrt auch den Zugang)' : 'Aktivieren'}
                              data-testid={`toggle-employee-button-${e.id}`}>
                        {e.active ? <UserX size={14} className="text-rose-600"/> : <UserCheck size={14} className="text-emerald-600"/>}
                      </Button>}
                      {canDelete && <Button size="sm" variant="ghost" onClick={() => loeschen(e)}
                              className="text-rose-600" title="Löschen (nur ohne Historie möglich)"
                              data-testid={`delete-employee-button-${e.id}`}>
                        <Trash2 size={14}/>
                      </Button>}
                      {!canEdit && !canDelete && <span className="text-xs text-slate-400">–</span>}
                    </TableCell>
                  </TableRow>
                );
              })}
              {loading && <TableRow><TableCell colSpan={8} className="text-center"><Loader2 className="inline animate-spin" size={14}/></TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <EmployeeDialog open={editOpen} onOpenChange={setEditOpen} editing={editing} setEditing={setEditing} costCenters={costCenters} onSave={save} />
      <PersonDialog open={anlegenOpen} onOpenChange={setAnlegenOpen} modus="mitarbeiter" onSaved={load} />
      {kontoFuer && (
        <ZugangNachreichenDialog
          employee={kontoFuer}
          onClose={() => setKontoFuer(null)}
          onSaved={() => { setKontoFuer(null); load(); }}
        />
      )}
      {schoolEmp && <SchoolPatternDialog open={schoolOpen} onOpenChange={setSchoolOpen} employee={schoolEmp} onChanged={load} />}
    </div>
  );
}

function EmployeeDialog({ open, onOpenChange, editing, setEditing, costCenters, onSave }) {
  if (!editing) return null;
  const isLeih = editing.type === 'Leiharbeiter' || editing.type === 'Praktikant';
  const istBuero = (editing?.assignment_context || 'construction') === 'office';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Mitarbeiter bearbeiten</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Vorname</Label><Input value={editing.first_name} onChange={e => setEditing({ ...editing, first_name: e.target.value })} data-testid="employee-firstname"/></div>
            <div><Label>Nachname</Label><Input value={editing.last_name} onChange={e => setEditing({ ...editing, last_name: e.target.value })} data-testid="employee-lastname"/></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Typ</Label>
              <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v })}>
                <SelectTrigger data-testid="employee-type"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {['Mitarbeiter', 'Azubi', 'Leiharbeiter', 'Praktikant'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Kostenstelle {!istBuero && <span className="text-rose-600">*</span>}</Label>
              {/* Bei Büro/Leitung darf "keine" stehen: Projektleitung und
                  Geschäftsführung arbeiten über alle Kostenstellen hinweg.
                  Der leere Text (nicht null) ist Absicht – das Backend
                  verwirft null-Werte beim Ändern und würde die alte
                  Kostenstelle sonst stehen lassen. */}
              <Select value={editing.cost_center_id || (istBuero ? '__none__' : '')}
                      onValueChange={(v) => setEditing({ ...editing, cost_center_id: v === '__none__' ? '' : v })}>
                <SelectTrigger data-testid="employee-kst"><SelectValue placeholder="Wählen…"/></SelectTrigger>
                <SelectContent>
                  {istBuero && <SelectItem value="__none__">Keine – alle Kostenstellen</SelectItem>}
                  {costCenters.filter(c => !c.deleted).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {istBuero && (
                <p className="text-xs text-slate-500 mt-1">
                  Ohne Kostenstelle gilt in der Auswertung „alle" – passend für Projektleitung
                  und Geschäftsführung.
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Eintrittsdatum</Label><Input type="date" value={editing.start_date} onChange={e => setEditing({ ...editing, start_date: e.target.value })} data-testid="employee-start-date"/></div>
            <div>
              <Label>Enddatum {isLeih && <span className="text-rose-600">*</span>}</Label>
              <Input type="date" value={editing.end_date || ''} onChange={e => setEditing({ ...editing, end_date: e.target.value || null })} data-testid="employee-end-date"/>
            </div>
          </div>
          <div>
            <Label>Zuordnung</Label>
            <Select value={editing.assignment_context || 'construction'} onValueChange={(v) => setEditing({ ...editing, assignment_context: v })}>
              <SelectTrigger data-testid="employee-context"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="construction">Baustellen (normale Plantafel)</SelectItem>
                <SelectItem value="device">Geräte (Geräte-Plantafel)</SelectItem>
                <SelectItem value="office">Büro / Leitung (wird nicht eingeplant)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">
              "Geräte" = z.B. Kranfahrer, erscheint ausschließlich in der Geräte-Plantafel.
              "Büro / Leitung" = Projektleitung und Geschäftsführung: taucht auf keiner Plantafel
              auf und wird nicht als "heute nicht eingeplant" gemeldet – Urlaub, Krankheit und
              Stundenzettel laufen aber ganz normal.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={editing.active ?? true} onChange={e => setEditing({ ...editing, active: e.target.checked })}/>
            Aktiv
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={onSave} data-testid="employee-save-button">Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SchoolPatternDialog({ open, onOpenChange, employee, onChanged }) {
  const [patterns, setPatterns] = useState([]);
  const [selectedDays, setSelectedDays] = useState(new Set());
  const [startKw, setStartKw] = useState(15);
  const [endKw, setEndKw] = useState(40);
  const [year, setYear] = useState(new Date().getFullYear());

  const load = async () => {
    const r = await api.get('/school-patterns', { params: { employee_id: employee.id } });
    setPatterns(r.data);
  };
  useEffect(() => { if (open) load(); /* eslint-disable-line */ }, [open, employee.id]);

  const toggleDay = (d) => {
    const next = new Set(selectedDays);
    if (next.has(d)) next.delete(d); else next.add(d);
    setSelectedDays(next);
  };

  const addPatterns = async () => {
    if (selectedDays.size === 0) { toast.error('Mindestens einen Wochentag wählen.'); return; }
    try {
      for (const d of selectedDays) {
        await api.post('/school-patterns', { employee_id: employee.id, weekday: d, start_kw: Number(startKw), end_kw: Number(endKw), year: Number(year), active: true });
      }
      toast.success(`${selectedDays.size} Schulmuster erstellt.`);
      setSelectedDays(new Set());
      await load();
      onChanged?.();
    } catch (err) { toast.error(extractError(err)); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/school-patterns/${id}`);
      toast.success('Muster gelöscht.');
      await load();
      onChanged?.();
    } catch (err) { toast.error(extractError(err)); }
  };

  // Summarize existing patterns by (year, kw-range)
  const existingDays = new Set(patterns.map(p => p.weekday));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Schulmuster für {employee.first_name} {employee.last_name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Wochentage wählen</Label>
            <div className="flex gap-1.5 mt-1.5">
              {WEEKDAYS.map(w => {
                const exists = existingDays.has(w.value);
                const selected = selectedDays.has(w.value);
                return (
                  <button
                    key={w.value}
                    type="button"
                    onClick={() => toggleDay(w.value)}
                    className={`flex-1 py-2 rounded border text-sm font-bold transition ${selected ? 'bg-amber-500 text-white border-amber-600 shadow-sm' : exists ? 'bg-amber-100 text-amber-900 border-amber-300' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
                    title={exists ? 'Bereits angelegt' : ''}
                  >
                    {w.label}{exists ? ' ✓' : ''}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-500 mt-1">Orange markierte Tage haben bereits ein Muster.</p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Von KW</Label><Input type="number" min="1" max="53" value={startKw} onChange={e => setStartKw(e.target.value)}/></div>
            <div><Label className="text-xs">Bis KW</Label><Input type="number" min="1" max="53" value={endKw} onChange={e => setEndKw(e.target.value)}/></div>
            <div><Label className="text-xs">Jahr</Label><Input type="number" value={year} onChange={e => setYear(e.target.value)}/></div>
          </div>
          <Button onClick={addPatterns} size="sm" disabled={selectedDays.size === 0} data-testid="add-school-pattern"><Plus size={14} className="mr-1"/>{selectedDays.size} Muster hinzufügen</Button>
          <div className="border-t pt-3">
            <div className="text-sm font-semibold mb-2">Bestehende Muster ({patterns.length})</div>
            <div className="space-y-1.5 max-h-48 overflow-y-auto scroll-thin">
              {patterns.length === 0 && <div className="text-sm text-slate-500">Keine Muster vorhanden.</div>}
              {patterns.map(p => (
                <div key={p.id} className="flex items-center justify-between rounded border border-slate-200 p-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white">{WEEKDAYS[p.weekday]?.label}</span>
                    <span>{WEEKDAYS[p.weekday]?.full}, KW{p.start_kw}–{p.end_kw}/{p.year}</span>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => del(p.id)} className="text-rose-600 h-7">Löschen</Button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Reicht einen Zugang für einen Mitarbeiter nach, der noch keinen hat.
 *
 * Gebraucht für Altbestände: Mitarbeiter, die angelegt wurden, bevor jeder
 * Mitarbeiter zwingend einen Zugang bekam. Name und Kostenstelle stehen schon
 * fest, gefragt wird nur nach den Anmeldedaten.
 */
function ZugangNachreichenDialog({ employee, onClose, onSaved }) {
  const [form, setForm] = useState({ email: '', username: '', password: '', role: 'viewer' });
  const [saving, setSaving] = useState(false);
  const setzen = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const speichern = async () => {
    if (!form.email.trim() && !form.username.trim()) {
      toast.error('Bitte eine E-Mail-Adresse oder einen Benutzernamen angeben.'); return;
    }
    if (form.email.trim() && !form.email.includes('@')) {
      toast.error('Die E-Mail-Adresse sieht nicht richtig aus.'); return;
    }
    if (form.password.length < 10) { toast.error('Das Passwort muss mindestens 10 Zeichen haben.'); return; }
    setSaving(true);
    try {
      await api.post(`/employees/${employee.id}/account`, {
        first_name: employee.first_name,
        last_name: employee.last_name,
        email: form.email.trim() || null,
        username: form.username.trim() || null,
        password: form.password,
        role: form.role,
      });
      toast.success(`Zugang für ${employee.first_name} ${employee.last_name} angelegt.`);
      onSaved();
    } catch (err) { toast.error(extractError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display">
            Zugang für {employee.first_name} {employee.last_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>E-Mail</Label>
            <Input type="email" value={form.email} onChange={e => setzen('email', e.target.value)} data-testid="nachreichen-email" />
          </div>
          <div>
            <Label>Benutzername</Label>
            <Input value={form.username} onChange={e => setzen('username', e.target.value)}
                   placeholder="z. B. b.kaufmann" data-testid="nachreichen-username" />
            <p className="text-xs text-slate-500 mt-1">
              Eines von beiden genügt. Angemeldet wird sich mit dem, was ausgefüllt ist.
            </p>
          </div>
          <div>
            <Label>Startpasswort *</Label>
            <Input type="password" value={form.password} onChange={e => setzen('password', e.target.value)} data-testid="nachreichen-password" />
            <p className="text-xs text-slate-500 mt-1">Mind. 10 Zeichen, 1 Zahl, 1 Buchstabe. Muss beim ersten Anmelden geändert werden.</p>
          </div>
          <div>
            <Label>Rolle</Label>
            <Select value={form.role} onValueChange={v => setzen('role', v)}>
              <SelectTrigger data-testid="nachreichen-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Administrator</SelectItem>
                <SelectItem value="projektleiter">Projektleiter</SelectItem>
                <SelectItem value="viewer">Betrachter</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={speichern} disabled={saving} data-testid="nachreichen-save">
            {saving ? 'Speichere…' : 'Zugang anlegen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
